<script setup lang="ts">
import type { SubjectSearchItem } from '@/models/current-student-advanced-standing-form/api'
import { displaySubject } from '@/models/current-student-advanced-standing-form/display'
import type { AlertTone } from '@/models/current-student-advanced-standing-form/modalAlert'
import LegacyAutocompleteDropdown from '@/components/current-student-advanced-standing-form/shared/LegacyAutocompleteDropdown.vue'
import ModalAlert from '@/components/current-student-advanced-standing-form/shared/ModalAlert.vue'

defineProps<{
  shown: boolean
  active: boolean
  searchText: string
  electiveCode: string
  suggestions: SubjectSearchItem[]
  activeSuggestionIndex: number
  electiveOptions: SubjectSearchItem[]
  errorMessage: string
  alertTone: AlertTone
  saving: boolean
}>()

const emit = defineEmits<{
  'update:searchText': [value: string]
  'update:electiveCode': [value: string]
  searchInput: []
  searchKeydown: [event: KeyboardEvent]
  searchBlur: []
  activateSuggestion: [index: number]
  selectSubject: [subject: SubjectSearchItem]
  electiveChange: []
  close: []
  save: []
}>()

function onSearchInput(event: Event): void {
  emit('update:searchText', (event.target as HTMLInputElement).value)
  emit('searchInput')
}

function onElectiveChange(event: Event): void {
  emit('update:electiveCode', (event.target as HTMLSelectElement).value)
  emit('electiveChange')
}

function subjectSuggestionKey(item: unknown): string {
  return (item as SubjectSearchItem).subject_code
}

function subjectSuggestionLabel(item: unknown): string {
  const subject = item as SubjectSearchItem
  return subject.value ?? displaySubject(subject)
}
</script>

<template>
  <div
    class="modal fade"
    id="LtuSubjectModal"
    :class="{ show: shown }"
    :style="{ display: active ? 'block' : 'none' }"
    role="dialog"
    aria-modal="true"
  >
    <div class="modal-dialog modal-lg" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">Edit La Trobe Subject</h4>
          <button type="button" class="modal-close" aria-label="Close" @click="emit('close')">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-field requiredinput form-autocomplete-field">
            <label for="ltuSubjectCodeInput">What specific La Trobe subject would you like to receive credit for:</label>
            <input
              id="ltuSubjectCodeInput"
              :value="searchText"
              class="form-control"
              autocomplete="off"
              @input="onSearchInput"
              @keydown="emit('searchKeydown', $event)"
              @blur="emit('searchBlur')"
            >
            <LegacyAutocompleteDropdown
              :items="suggestions"
              :visible="suggestions.length > 0"
              :active-index="activeSuggestionIndex"
              :item-key="subjectSuggestionKey"
              :item-label="subjectSuggestionLabel"
              @activate="emit('activateSuggestion', $event)"
              @select="emit('selectSubject', $event as SubjectSearchItem)"
            />
          </div>
          OR
          <div class="form-field requiredinput">
            <label for="ltuSubjectElectiveCodeInput">Alternatively, you can choose an unspecified discipline elective subject:</label>
            (E.g. HIS215 - 2nd year history subject)
            <select
              id="ltuSubjectElectiveCodeInput"
              :value="electiveCode"
              class="form-select"
              @change="onElectiveChange"
            >
              <option value=""></option>
              <option
                v-for="subject in electiveOptions"
                :key="subject.subject_code"
                :value="subject.subject_code"
              >
                {{ subject.subject_code }} - {{ subject.subject_name }}
              </option>
            </select>
          </div>

          <ModalAlert alert-id="LtuSubjectModalError" :alert-tone="alertTone" :message="errorMessage" />
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-primary" :disabled="saving" @click="emit('close')">Cancel</button>
          <button type="button" class="btn btn-success" id="LtuSubjectModalSaveBtn" :disabled="saving" @click="emit('save')">Save</button>
        </div>
      </div>
    </div>
  </div>
</template>
